import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  getFullHoursMinutesMaskDuration,
  useFormattedDuration
} from 'modules/notes/common';
import { DiaryEntrySyncStatus } from 'graphql/types';
import { SmartDiaryEntry } from '../../type';
import { useSmartEntryUnselectState } from '../../hooks/useSmartEntryUnselectState';
import { useSmartDiaryTotalDuration } from '.';

type SmartDiaryTotalsReturnType = {
  checked: boolean;
  indeterminate: boolean;
  checkedCount: number;
  totalDuration: string;
  onChange: () => void;
  disabled: boolean;
  fullhoursMinuteDuration: string;
};

export const useSmartDiaryTotals = (
  smartEntryMap: Record<string, SmartDiaryEntry>
): SmartDiaryTotalsReturnType => {
  const { watch, control, setValue, getValues } = useFormContext();
  const {
    addUnselectedEntries,
    removeUnselectedEntries
  } = useSmartEntryUnselectState();
  const updatePendingIds = useMemo(
    () =>
      Object.values(smartEntryMap)
        .filter(
          (entry) => entry.syncStatus === DiaryEntrySyncStatus.UpdatePending
        )
        .map((entry) => entry.id),
    [smartEntryMap]
  );
  const defaultValue = useMemo(
    () =>
      Object.keys(smartEntryMap).reduce((acc, key) => {
        return { ...acc, [key]: Boolean(getValues()[key] ?? false) };
      }, {} as Record<string, boolean>),
    [getValues, smartEntryMap]
  );
  const formValues = useWatch({
    name: Object.keys(smartEntryMap),
    control,
    defaultValue
  });
  const [checked, setChecked] = useState<boolean>(false);
  const [indeterminate, setIndeterminate] = useState<boolean>(false);
  const [checkedCount, setCheckedCount] = useState<number>(0);

  const result = useMemo<Record<string, boolean>>(
    () =>
      Object.keys(smartEntryMap).reduce(
        (acc, item, index) => ({
          ...acc,
          [`${item}`]: formValues[index] ?? false
        }),
        {}
      ),
    [formValues, smartEntryMap]
  );

  const totalDuration = useSmartDiaryTotalDuration(result, smartEntryMap);

  const formattedDuration = useFormattedDuration({
    needsMask: true,
    duration: totalDuration
  });
  const fullhoursMinuteDuration = getFullHoursMinutesMaskDuration(
    totalDuration
  );

  const onChange = useCallback(() => {
    if (indeterminate) {
      addUnselectedEntries(updatePendingIds);
      setChecked(false);
      Object.keys(result).forEach((key) =>
        setValue(key, false, { shouldDirty: true })
      );
      return;
    }
    setIndeterminate(false);
    Object.keys(result).forEach((key) =>
      setValue(key, !checked, { shouldDirty: true })
    );
    setChecked(!checked);
    !checked
      ? removeUnselectedEntries(updatePendingIds)
      : addUnselectedEntries(updatePendingIds);
  }, [
    addUnselectedEntries,
    checked,
    indeterminate,
    removeUnselectedEntries,
    result,
    setValue,
    updatePendingIds
  ]);

  useEffect(() => {
    if (!Object.keys(smartEntryMap).length) {
      setCheckedCount(0);
      setChecked(false);
      return;
    }
    const watchResult = watch(Object.keys(smartEntryMap));
    const result: Record<string, boolean> = Object.keys(smartEntryMap).reduce(
      (acc, item, index) => ({
        ...acc,
        [`${item}`]: watchResult[index] ?? false
      }),
      {}
    );

    setIndeterminate(false);
    const noteKeys = Object.keys(result);
    if (noteKeys.length > 0) {
      const checkedNotes = noteKeys.filter((key) => result[key]);
      setCheckedCount(checkedNotes.length);
      if (checkedNotes.length === noteKeys.length) {
        setChecked(true);
        return;
      }
      if (checkedNotes.length > 0) {
        setIndeterminate(true);
        return;
      }
      setChecked(false);
    }
  }, [result, smartEntryMap, watch]);
  return useMemo(
    () => ({
      checked,
      indeterminate,
      checkedCount,
      onChange,
      totalDuration: formattedDuration,
      fullhoursMinuteDuration,
      disabled: Object.keys(result).length === 0
    }),
    [
      checked,
      indeterminate,
      checkedCount,
      onChange,
      formattedDuration,
      fullhoursMinuteDuration,
      result
    ]
  );
};
